import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Cell
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrophy,
    faExclamationTriangle,
    faChartLine,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import styles from './SuperAdminAnalytics.module.css';

/** ------------------------------------------------------------------
 * DUMMY DATA
 * ------------------------------------------------------------------ */

const schoolEngagementData = [
    { name: 'Dav Public', attempts: 1200 },
    { name: 'St. Xavier', attempts: 980 },
    { name: 'Ryan Intl', attempts: 850 },
    { name: 'DPS Delhi', attempts: 720 },
    { name: 'Vibgyor', attempts: 600 },
];

const schoolPerformanceData = [
    { name: 'St. Xavier', score: 92 },
    { name: 'Dav Public', score: 88 },
    { name: 'Ryan Intl', score: 85 },
    { name: 'DPS Delhi', score: 78 },
    { name: 'Vibgyor', score: 72 },
];

const lowPerformingSchools = [
    { name: 'City High', score: 45 },
    { name: 'Govt Model', score: 42 },
    { name: 'Rural East', score: 38 },
];

const inactiveSchools = [
    { name: 'North Valley', lastActive: '45 days ago' },
    { name: 'Sunny Side', lastActive: '32 days ago' },
    { name: 'Greenfield', lastActive: '60 days ago' },
];

/** ------------------------------------------------------------------
 * STYLING CONSTANTS
 * ------------------------------------------------------------------ */

const COLORS = {
    blue: '#3b82f6',
    green: '#10b981',
    red: '#f87171',
    grid: 'rgba(255, 255, 255, 0.08)',
    text: '#94a3b8'
};

const CHART_TOOLTIP_STYLE = {
    backgroundColor: '#1e293b',
    borderColor: '#334155',
    color: '#f1f5f9',
    borderRadius: '8px'
};

/** ------------------------------------------------------------------
 * MAIN COMPONENT
 * ------------------------------------------------------------------ */

const SuperAdminAnalytics: React.FC = () => {
    return (
        <div className={styles.container}>
            <h1 className={styles.title}>
                SuperAdmin Analytics
            </h1>

            <div className={styles['grid-2']}>
                {/* 1. Top Engaged Schools (Horizontal Bar Chart) */}
                <div className={styles.card}>
                    <h3 className={styles['card-header']}>
                        <FontAwesomeIcon icon={faChartLine} className={styles['card-icon']} style={{ color: COLORS.blue }} />
                        Top Engaged Schools
                    </h3>
                    <div className={styles['chart-container']}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={schoolEngagementData}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={COLORS.grid} />
                                <XAxis
                                    type="number"
                                    domain={[0, 1200]}
                                    ticks={[0, 300, 600, 900, 1200]}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text, fontSize: 12 }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text, fontSize: 13 }}
                                    width={100}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                                    contentStyle={CHART_TOOLTIP_STYLE}
                                />
                                <Bar dataKey="attempts" fill={COLORS.blue} radius={[0, 4, 4, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 2. Top Performing Schools (Vertical Bar Chart) */}
                <div className={styles.card}>
                    <h3 className={styles['card-header']}>
                        <FontAwesomeIcon icon={faTrophy} className={styles['card-icon']} style={{ color: '#fbbf24' }} />
                        Top Performing Schools
                    </h3>
                    <div className={styles['chart-container']}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={schoolPerformanceData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text, fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text, fontSize: 12 }}
                                    domain={[0, 100]}
                                    ticks={[0, 25, 50, 75, 100]}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                                    contentStyle={CHART_TOOLTIP_STYLE}
                                />
                                <Bar dataKey="score" fill={COLORS.green} radius={[4, 4, 0, 0]} barSize={35} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 3. Lowest Performing (Attention Needed) (Vertical Bar Chart) */}
                <div className={styles.card}>
                    <h3 className={styles['card-header']}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className={styles['card-icon']} style={{ color: COLORS.red }} />
                        Lowest Performing (Attention Needed)
                    </h3>
                    <div className={styles['chart-container']}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={lowPerformingSchools} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text, fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: COLORS.text, fontSize: 12 }}
                                    domain={[0, 100]}
                                    ticks={[0, 25, 50, 75, 100]}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                                    contentStyle={CHART_TOOLTIP_STYLE}
                                />
                                <Bar dataKey="score" fill={COLORS.red} radius={[4, 4, 0, 0]} barSize={35} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 4. Inactive Schools (Table) */}
                <div className={styles.card}>
                    <h3 className={styles['card-header']}>
                        <FontAwesomeIcon icon={faInfoCircle} className={styles['card-icon']} style={{ color: COLORS.text }} />
                        Inactive Schools
                    </h3>
                    <div className={styles['analytics-table-container']}>
                        <table className={styles['analytics-table']}>
                            <thead>
                                <tr>
                                    <th>School Name</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Last Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inactiveSchools.map((school, index) => (
                                    <tr key={index}>
                                        <td>{school.name}</td>
                                        <td>
                                            <span className={`${styles['status-indicator']} ${styles['status-red']}`}></span>
                                        </td>
                                        <td style={{ textAlign: 'right' }} className={styles['last-active']}>
                                            {school.lastActive}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminAnalytics;
